import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@mui/material";
import { PageContainer, toast } from "@outpave/ds";
import SecretRevealDialog from "components/common/SecretRevealDialog";
import WebhookEndpointDrawer from "components/platform/webhooks/WebhookEndpointDrawer";
import WebhookEndpointsTable from "components/platform/webhooks/WebhookEndpointsTable";
import WebhookEndpointStatus from "enums/WebhookEndpointStatus";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";
import {
  useWebhookEndpoints,
  type WebhookEndpoint,
  type WebhookEndpointWithSecret,
} from "hooks/useWebhookEndpoints";
import { toastMutationError } from "utils/toastMutationError";

type Revealed = { result: WebhookEndpointWithSecret; rotated: boolean };

const PlatformWebhooks: React.FC = () => {
  const { t } = useTranslation();
  const { user: loggedInUser } = useGetLoggedInUser();
  const breadcrumbs = useNavBreadcrumbs(t("Webhooks"));
  const platformId = loggedInUser?.platformId;
  const {
    endpoints,
    eventTypes,
    loading,
    error,
    createWebhookEndpoint,
    updateWebhookEndpoint,
    deleteWebhookEndpoint,
    rotateWebhookSecret,
  } = useWebhookEndpoints({ skip: !platformId });
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Kept after the drawer closes, so its title does not flip while it animates out.
  const [editing, setEditing] = useState<WebhookEndpoint | null>(null);
  const [revealed, setRevealed] = useState<Revealed | null>(null);

  const openDrawer = (endpoint: WebhookEndpoint | null) => {
    setEditing(endpoint);
    setDrawerOpen(true);
  };

  // Each handler reports a failure and rethrows, so a confirmation stays open to retry.
  const handleSetPaused = async (
    endpoint: WebhookEndpoint,
    paused: boolean,
  ) => {
    try {
      await updateWebhookEndpoint(endpoint.id, {
        status: paused
          ? WebhookEndpointStatus.PAUSED
          : WebhookEndpointStatus.ACTIVE,
        revision: endpoint.revision,
      });
      toast.success(
        paused ? t("WebhookEndpointPaused") : t("WebhookEndpointResumed"),
      );
    } catch (err: unknown) {
      toastMutationError(err, t("WebhookEndpointStatusError"));
      throw err;
    }
  };

  const handleRotate = async (endpoint: WebhookEndpoint) => {
    try {
      setRevealed({
        result: await rotateWebhookSecret(endpoint.id),
        rotated: true,
      });
    } catch (err: unknown) {
      toastMutationError(err, t("WebhookRotateError"));
      throw err;
    }
  };

  const handleDelete = async (endpoint: WebhookEndpoint) => {
    try {
      await deleteWebhookEndpoint(endpoint.id);
      toast.success(t("WebhookEndpointDeleted"));
    } catch (err: unknown) {
      toastMutationError(err, t("WebhookEndpointDeleteError"));
      throw err;
    }
  };

  return (
    <PageContainer
      breadcrumbs={breadcrumbs}
      title={t("Webhooks")}
      subtitle={t("PlatformWebhooksDescription")}
      primaryAction={
        <Button
          variant="primary-filled"
          onClick={() => openDrawer(null)}
          disabled={!platformId}
        >
          {t("Add endpoint")}
        </Button>
      }
    >
      {platformId ? (
        <>
          <WebhookEndpointsTable
            endpoints={endpoints}
            loading={loading}
            loadError={!!error}
            onEdit={openDrawer}
            onSetPaused={handleSetPaused}
            onRotate={handleRotate}
            onDelete={handleDelete}
          />
          <WebhookEndpointDrawer
            open={drawerOpen}
            endpoint={editing}
            eventTypes={eventTypes}
            onClose={() => setDrawerOpen(false)}
            onCreate={createWebhookEndpoint}
            onUpdate={updateWebhookEndpoint}
            onSecretReveal={(result) => setRevealed({ result, rotated: false })}
          />
          <SecretRevealDialog
            open={!!revealed}
            title={
              revealed?.rotated
                ? t("Secret rotated")
                : t("Webhook endpoint added")
            }
            subtitle={revealed?.result.webhookEndpoint.url}
            body={
              revealed?.rotated
                ? t("WebhookSecretRotatedBody")
                : t("WebhookSecretBody")
            }
            secretLabel={t("Signing secret")}
            secret={revealed?.result.secret ?? null}
            onClose={() => setRevealed(null)}
          />
        </>
      ) : (
        <Typography sx={{ color: "var(--color-text-secondary)" }}>
          {t("PlatformWebhooksUnavailable")}
        </Typography>
      )}
    </PageContainer>
  );
};

export default PlatformWebhooks;
